import { Stack } from '@mui/material';
import { DateTime } from 'luxon';

import { CampaignChannels, SendRecurrenceFrequency } from 'domains/campaigns/types';
import { getIsRecurrenceContinuous, getIsRecurrenceEnabled, getRecurrenceLabel } from 'domains/campaigns/utils';
import { isTemplate } from 'domains/content/types';
import ButtonContentPreview from 'domains/core/components/AutocompleteContent/ButtonContentPreview';
import { formatDateTime } from 'domains/core/components/DateRangeText/DateRangeText';
import DetailItem from 'domains/core/components/DetailItem';
import useAppSendSenderProfileTemplate from 'hooks/queries/useAppSendSenderProfileTemplate';
import useDirectMail from 'hooks/queries/useDirectMail';
import useSenderProfileVoice from 'hooks/queries/useSenderprofileVoice';
import useTemplate from 'hooks/queries/useTemplate';
import useVoiceContentById from 'hooks/queries/useVoiceContentById';
import useSmsData from 'pages/ContentSmsBuilderPage/hooks/useSmsData';
import { formatPhoneNumber, getFormattedLocalTime } from 'utils';

type Props = {
    channel?: CampaignChannels;
    firstEmailRecurrenceFrequency?: SendRecurrenceFrequency;
    header?: string;
    isFirstEmail?: boolean;
    isPreviewOpen: boolean;
    recurrenceFrequency?: SendRecurrenceFrequency;
    scheduledEndTimestamp?: string;
    scheduledTimestamp?: string;
    timeDelay?: string;
    isSendTimeABTest?: boolean;
    onClick?: () => void;
};

type ContentDetailItemProps = Props & {
    contentName: string;
};

type ContentDetailByIdProps = Props & {
    contentId: number;
    isSmsLegacy?: boolean;
};

type ContentDetailProps = ContentDetailByIdProps & {
    isCampaign?: boolean;
};

type VoiceCallerDetailByIdProps = Props & {
    callerId: number;
};

const ContentDetail = ({ channel, isCampaign, isSmsLegacy, ...rest }: ContentDetailProps) => {
    if (channel === CampaignChannels.DIRECT_MAIL) {
        return <ContentDetailByDirectMailId {...{ channel }} {...rest} />;
    }
    if (channel === CampaignChannels.SMS && !isSmsLegacy) {
        return <ContentDetailBySmsContentId {...{ channel }} {...rest} />;
    }
    if (channel === CampaignChannels.VOICE_CALL) {
        return <ContentDetailByVoiceContentId {...{ channel }} {...rest} />;
    }
    if (isCampaign) {
        return <ContentDetailBySendId {...{ isSmsLegacy }} {...rest} />;
    }
    return <ContentDetailByTemplateId {...rest} />;
};

export default ContentDetail;

export const ContentDetailByDirectMailId = ({
    contentId,
    isSmsLegacy: _isSmsLegacy,
    ...rest
}: ContentDetailByIdProps) => {
    const { data, isLoading, isSuccess } = useDirectMail(contentId, {
        enabled: Boolean(contentId),
    });

    const getContentName = () => {
        if (isLoading) return 'Loading...';
        if (!isSuccess) return 'Problem getting name.';
        return data.name;
    };

    return <ContentDetailItem {...rest} contentName={getContentName()} />;
};

export const ContentDetailBySendId = ({ contentId, isSmsLegacy, onClick, ...rest }: ContentDetailByIdProps) => {
    const { data: contentDetail, isSuccess: isContentDetailSuccess } = useAppSendSenderProfileTemplate(contentId, {
        enabled: Boolean(contentId),
    });

    const getContentName = () => {
        if (!isContentDetailSuccess || !contentDetail) return '';

        return isSmsLegacy ? contentDetail.contentSnapshot : contentDetail.templateName;
    };

    return <ContentDetailItem {...rest} contentName={getContentName()} {...(!isSmsLegacy && { onClick })} />;
};

export const ContentDetailBySmsContentId = ({ contentId, ...rest }: ContentDetailByIdProps) => {
    const { smsData } = useSmsData({
        id: `${contentId}`,
        enabled: Boolean(contentId),
    });

    return <ContentDetailItem {...rest} contentName={smsData?.name} />;
};

export const ContentDetailByTemplateId = ({ contentId, ...rest }: ContentDetailByIdProps) => {
    const { data: template } = useTemplate({ templateId: `${contentId}` });
    const templateName = isTemplate(template) ? template.name : '';

    return <ContentDetailItem {...rest} contentName={templateName} />;
};

export const ContentDetailByVoiceContentId = ({ contentId, ...rest }: ContentDetailByIdProps) => {
    const { data: voiceContent, isSuccess } = useVoiceContentById({
        voiceContentId: `${contentId}`,
    });

    // Always render schedule/recurrence footer (same as SMS) — do not gate the whole
    // ContentDetailItem on voice-content fetch success, or monthly labels never appear.
    const contentName = isSuccess ? voiceContent?.name : undefined;
    return <ContentDetailItem {...rest} contentName={contentName} />;
};

export const VoiceCallerDetailById = ({ callerId }: VoiceCallerDetailByIdProps) => {
    const { data: voiceCaller, isSuccess } = useSenderProfileVoice(callerId);

    return (
        isSuccess && (
            <DetailItem
                description={`${voiceCaller.name}`}
                footer={`${voiceCaller.countryCode} ` + formatPhoneNumber(voiceCaller.number)}
                header={'caller id'}
            />
        )
    );
};

export const ContentDetailItem = ({
    channel,
    firstEmailRecurrenceFrequency,
    header,
    isFirstEmail,
    isPreviewOpen,
    recurrenceFrequency,
    scheduledEndTimestamp,
    scheduledTimestamp,
    timeDelay,
    isSendTimeABTest,
    contentName,
    onClick,
}: ContentDetailItemProps) => {
    const renderScheduledInfo = () => {
        const formattedScheduledTimestamp = formatDateTime(scheduledTimestamp);
        const isRecurringCampaign = getIsRecurrenceEnabled(firstEmailRecurrenceFrequency);

        if (isFirstEmail) {
            const recurrenceLabel = getRecurrenceLabel(recurrenceFrequency, scheduledTimestamp);
            const recurrenceLabelForDislay = recurrenceLabel.charAt(0).toUpperCase() + recurrenceLabel.slice(1);

            const hasEndDate = !!scheduledEndTimestamp;
            const formattedScheduledEndTimestamp = formatDateTime(scheduledEndTimestamp);

            return (
                <Stack>
                    {isRecurringCampaign && (
                        <div data-testid="curations-summary-recurrence-label">{recurrenceLabelForDislay}</div>
                    )}
                    <div>
                        {formattedScheduledTimestamp}
                        {hasEndDate && ' -'}
                    </div>
                    {hasEndDate && (
                        <div data-testid="curations-summary-scheduled-end-timestamp">
                            {formattedScheduledEndTimestamp}
                        </div>
                    )}
                </Stack>
            );
        }

        if (isRecurringCampaign) {
            const formattedScheduledTime = getFormattedLocalTime(DateTime.fromISO(scheduledTimestamp).toISO());
            const isRecurrenceContinuous = getIsRecurrenceContinuous(firstEmailRecurrenceFrequency);
            return (
                <Stack>
                    {timeDelay}s after previous send{isRecurrenceContinuous ? '' : ` at ${formattedScheduledTime}`}
                </Stack>
            );
        }

        return <Stack>{formattedScheduledTimestamp}</Stack>;
    };

    return (
        <DetailItem
            description={contentName}
            descriptionIcon={
                !!onClick ? (
                    <ButtonContentPreview contentType={channel} isActive={isPreviewOpen} onClick={onClick} />
                ) : null
            }
            header={header}
            footer={!isSendTimeABTest ? renderScheduledInfo() : ''}
            isActive={isPreviewOpen}
        />
    );
};
